import { createSlice, createSelector, createEntityAdapter } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { getDevicesCombined } from '../actions/deviceActions';
import { Device } from './device';
import { getAllCommands } from '../actions/hunterActions';
import { LatLat } from './user';

export const hunterAdapter = createEntityAdapter();

export type Command = {
    model_name: string;
};
export type TrailDuration = Record<string, number>;
export type TrailColor = Record<string, string>;

export type UserTrail = {
    user_id: number;
    device_id: number;
    positions: LatLat[];
};
const INITIAL_STATE = {
    isLoading: false,
    hunterData: null as Device | null,
    deviceInfo: null as Device | null,
    trail: [],
    trails: [] as UserTrail[],
    trailColors: {} as TrailColor,
    trailDurations: {} as TrailDuration,
    batteryLevel: null,
    signalStrength: null,
    historyPanelStatus: false,
    historyActiveIndex: 0,
    historyActiveSliderIndex: 0,
    historyMotionSpeed: 1000,
    historyData: [
        {
            coords: [],
            nodes: [],
            totalDistance: 0,
            topSpeed: 0,
            barkingTime: 0,
            bpm: 0,
        },
    ],
    historyBarkingTrails: [],
    vibrationType: 'wave',
    showInProfile: [],
    hunterInfoVisible: false,
    hunterInfoBSTS: 0,
    vibroInfoVisible: false,
    dogsExtentedModal: false,
    allCommands: [] as Command[],
};

const hunterReducer = createSlice({
    name: 'hunter',
    initialState: hunterAdapter.getInitialState(INITIAL_STATE),
    reducers: {
        setHunterLoading(state, action) {
            state.isLoading = action.payload;
        },
        setHunterTrails(state, action) {
            state.trails = action.payload;
        },
        setDeviceInfo(state, action) {
            state.isLoading = false;
            state.deviceInfo = action.payload;
        },
        setHunterData(state, action) {
            let commands: Command[] = [];

            if ((action.payload?.myDevice || action.payload?.sharingAdmin) && action.payload?.data?.model) {
                commands = state.allCommands?.filter((c) => c.model_name === action.payload?.data?.model) ?? [];
            }

            state.isLoading = false;

            // console.log('setHunterData', action.payload);
            state.hunterData = { ...action.payload, commands };
        },
        setHunterTrailDuration(state, action) {
            state.trailDurations = { ...state.trailDurations, [action.payload.hunterId]: action.payload.duration };
        },
        setHunterTrailColor(state, action) {
            return { ...state, trailColors: { ...state.trailColors, [action.payload.hunterId]: action.payload.color } };
        },
    },
    extraReducers: (builder) => {
        builder

            .addCase(getDevicesCombined.fulfilled, (state, action) => {
                state.trails = action.payload.hunterTrails;
            })
            .addCase(getDevicesCombined.rejected, () => {})
            .addCase(getAllCommands.fulfilled, (state, action) => {
                state.allCommands = action.payload;
            })
            .addCase(getAllCommands.rejected, () => {});
    },
});

export const {
    setHunterLoading,
    setHunterTrails,
    setHunterData,
    setDeviceInfo,
    setHunterTrailDuration,
    setHunterTrailColor,
} = hunterReducer.actions;

export default hunterReducer.reducer;
const userState$ = (s: RootState) => s.user;
const hunterState$ = (s: RootState) => s.hunter;
const mapState$ = (s: RootState) => s.map;
export const rawTrails$ = createSelector(hunterState$, (s) => s.trails);
export const userData$ = createSelector(userState$, (s) => s.data);
export const trailDurations$ = createSelector(hunterState$, (s) => s.trailDurations);
export const mapFilters$ = createSelector(mapState$, (s) => s.mapFilters);

export const filteredHuntersTrails$ = createSelector(
    rawTrails$,
    userData$,
    trailDurations$,
    (trails, userData, durations) =>
        trails
            .filter((t) => t.user_id !== userData?.id)
            .filter((trail) => {
                // console.log('typeof durations ', typeof durations === 'object' && durations[`${trail.user_id}`] != 0);
                return typeof durations === 'object' && durations[`${trail.user_id}`] != 0;
            })
            .map((trail) => {
                const positions = trail.positions.map((pos) => {
                    return {
                        ...pos,
                        lat: pos.latitude,
                        lng: pos.longitude,
                    };
                });

                return { ...trail, positions };
            }),
);
